import React, { useState } from "react";
import { LoadingButton } from "@/components/ui/loading-button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { SquarePlus, Info } from "lucide-react";
import { Promotion } from "@/constants/objects";
import { SelectSubscriptionPromotion } from "@/components/SelectSubscriptionPromotion";
import { postCsvData, validateEmail } from "@/services/csvService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAppDispatch } from "@/hooks/reducer-hooks";
import { getSubscriptionPromotions } from "@/services/userDataService";
import { setSubscriptionPromotions } from "@/slices/userSlice";

export function AddResidentDialog({ promotionsList }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const dispatch = useAppDispatch();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedPromotion) return;

    // Check if voucher has remaining slots
    if (selectedPromotion.type === "voucher" && selectedPromotion.amountRemaining === 0) {
      toast({
        title: "Cannot add resident",
        description: "This voucher has no remaining slots available.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      await postCsvData({
        emails: [email],
        importOption: "add",
        subscriptionPromotionLogicId: selectedPromotion.id,
      });

      // Refresh promotions data to get updated amounts
      const updatedPromotions = await getSubscriptionPromotions();
      dispatch(setSubscriptionPromotions(updatedPromotions));

      toast({
        title: "Resident Added",
        description: "The resident has been added successfully.",
        variant: "success",
      });
      setOpen(false);
      setEmail("");
      setSelectedPromotion(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setEmail("");
          setSelectedPromotion(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <LoadingButton className="ml-2" variant="default" onClick={() => setOpen(true)}>
          <SquarePlus className="pr-1" />
          Add Resident
        </LoadingButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Resident</DialogTitle>
          <DialogDescription>
            Please enter the email of the resident you want to add.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            placeholder="Enter resident email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <SelectSubscriptionPromotion
            promotions={promotionsList}
            setSelectedPromotion={setSelectedPromotion}
            selectedPromotion={selectedPromotion}
          />

          {selectedPromotion?.type === "voucher" && selectedPromotion.amountRemaining === 0 && (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertTitle>No slots available</AlertTitle>
              <AlertDescription>
                This voucher has no remaining slots available. Please select a different voucher or
                contact support.
              </AlertDescription>
            </Alert>
          )}

          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Important</AlertTitle>
            <AlertDescription>
              To apply the voucher, you need to use the email associated with resident's current
              TULU account. If they do not yet have a TULU account, please use the email they plan
              to sign up with.
            </AlertDescription>
          </Alert>

          {selectedPromotion?.type === "voucher" && (
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <h4 className="font-medium">How it works:</h4>
              <ul className="list-disc pl-4 space-y-1 text-sm">
                <li>
                  If a resident already has an active subscription, the free months will be added on
                  top of their current plan.
                </li>
                <li>
                  Once the resident signs up for TULU using the email linked to the voucher, the
                  free months will be automatically applied.
                </li>
                <li>
                  The benefits remain the same for both 3-month and 6-month vouchers:
                  <ul className="list-disc pl-4 mt-1">
                    <li>Free unlimited rentals for set hour</li>
                    <li>Access to shop with year round promotions, benefits and flash sales</li>
                    <li>Access to print</li>
                  </ul>
                </li>
              </ul>
            </div>
          )}

          <LoadingButton
            type="submit"
            loading={loading}
            className="w-full"
            disabled={
              !selectedPromotion ||
              !validateEmail(email) ||
              (selectedPromotion.type === "voucher" && selectedPromotion.amountRemaining === 0)
            }
          >
            {selectedPromotion?.type === "voucher" && selectedPromotion.amountRemaining === 0
              ? "No slots available"
              : "Submit"}
          </LoadingButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}
